import React, { useState, useEffect } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const heroSlides = [
    {
      id: 1,
      title: "Professional Cleaning Services You Can Trust",
      subtitle: "Transform your space with our expert cleaning and flooring solutions",
      image: "https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?auto=compress&cs=tinysrgb&w=1200",
      cta: "Book Free Consultation"
    },
    {
      id: 2,
      title: "Premium Flooring Installation & Restoration",
      subtitle: "From vinyl to hardwood, we deliver exceptional flooring solutions",
      image: "https://images.pixabay.com/photo/2017/03/28/12/11/chairs-2181947_1280.jpg",
      cta: "Explore Flooring Services"
    },
    {
      id: 3,
      title: "Emergency Cleaning Services Available 24/7",
      subtitle: "When disaster strikes, we're here to restore your property quickly",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80",
      cta: "Emergency Booking"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides?.length) % heroSlides?.length);
  };

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden bg-gray-900">
      {/* Background Slides */}
      {heroSlides?.map((slide, index) => (
        <div
          key={slide?.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ willChange: 'opacity' }}
        >
          <Image
            src={slide?.image}
            alt={slide?.title}
            className="w-full h-full object-cover object-center"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
      ))}
      
      {/* Content - Fixed alignment container */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="container mx-auto px-4 lg:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto lg:mx-0 lg:max-w-4xl">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {heroSlides?.[currentSlide]?.title}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto lg:mx-0">
                {heroSlides?.[currentSlide]?.subtitle}
              </p>
              
              {/* CTA Buttons - Improved alignment */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12 items-center sm:items-stretch">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  className="text-lg px-8 py-4 w-full sm:w-auto"
                >
                  {heroSlides?.[currentSlide]?.cta}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Phone"
                  iconPosition="left"
                  className="text-lg px-8 py-4 text-white border-white hover:bg-white hover:text-gray-900 w-full sm:w-auto"
                >
                  Call (555) 123-4567
                </Button>
              </div>

              {/* Trust Indicators - Better responsive alignment */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 text-white/80">
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={20} />
                  <span className="text-sm whitespace-nowrap">Fully Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Award" size={20} />
                  <span className="text-sm whitespace-nowrap">Certified Professionals</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={20} />
                  <span className="text-sm whitespace-nowrap">24/7 Emergency Service</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Star" size={20} />
                  <span className="text-sm whitespace-nowrap">4.9/5 Rating (500+ Reviews)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation Controls - Improved positioning */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-2">
          {heroSlides?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Arrow Navigation - Better positioning */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-all duration-200"
        aria-label="Previous slide"
        style={{ transform: 'translateY(-50%)' }}
      >
        <Icon name="ChevronLeft" size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-all duration-200"
        aria-label="Next slide"
        style={{ transform: 'translateY(-50%)' }}
      >
        <Icon name="ChevronRight" size={24} />
      </button>
    </section>
  );
};

export default HeroSection;